import React from 'react';
import { useSelector } from 'react-redux';
import { getMetricMetaByKey } from '@utils/metrics';
import { isRateMetric } from '@utils/metrics/rateMetrics';
import { Trend } from '@components/common';
import { UnsupportedMetricBadge } from '@components/common/UnsupportedMetric';
import humanizeUtil from '@utils/humanizeUtil';
import { useAtomValue } from 'jotai';
import { percentileAtom, unsupportedMetricsAtom } from '@src/components/metrics-panel-hz/atoms';
import { realTimeModeOnTrendsSelector } from '@store/common/selectors';
import { percentileConfig } from '@utils/metrics/constants';
import Toolbox from './Toolbox';
import DistributionToggle from './DistributionToggle';
import { useDodData } from '../hooks/useDodSeries';
import { useMetricData } from '../hooks/useMetricSeries';
import BooleanFilter from './Tools/BooleanFilter';
import { useLiveSummaryData } from '../hooks/useLiveSummary';
import { DATA_TAGS } from '../hooks/useData';
import {
  Subscript,
  HeaderWrapper,
  ValueWrapper,
  TitleWrapper,
  LiveModeMarker,
  SecondRow,
  StyledMetricTitle,
  StyledEllipsisTooltip,
} from './styled';
import { getDodDiff } from '@src/components/metrics-panel-hz/utils/calculateDodTotal';
import { useDrilldownMeta } from '@src/hooks/common/useDrilldownMeta';
import { getPercentileMetrics } from '@src/utils/metrics/metricHelpers';

function Header({
  metric,
  chartMetric,
  showTrend,
  chartMode,
  setChartMode,
  toggleLegend,
  distribution,
  selectDistribution,
  naMode,
}) {
  // to show totals and diff
  const { flowId, frustrationSignalId } = useDrilldownMeta();
  const { total: metricTotal } = useMetricData({ metric: chartMetric, dataTag: DATA_TAGS.primary });
  const { total: dodTotal } = useDodData({ metric: chartMetric, dataTag: DATA_TAGS.primaryDod });
  const isLiveModeSelected = useSelector(realTimeModeOnTrendsSelector);
  const trend = getDodDiff(metricTotal, dodTotal);
  const percentile = useAtomValue(percentileAtom);
  const { metrics: unsupportedMetrics, cause: unsupportedCause } =
    useAtomValue(unsupportedMetricsAtom);
  const isUnsupported =
    unsupportedMetrics.includes(metric) || unsupportedMetrics.includes(chartMetric);
  const metricInfo = getMetricMetaByKey({
    metricKey: chartMetric,
    flowOverride: !!flowId,
    frustrationSignalOverride: !!frustrationSignalId,
  });

  // The count toggle changes the value, not the identity of the rate card.
  const labelInfo = isRateMetric(metric) ? getMetricMetaByKey({ metricKey: metric }) : metricInfo;

  const liveSummaryData = useLiveSummaryData({ metric: chartMetric });
  const displayTotal = isLiveModeSelected ? liveSummaryData.total : metricTotal;
  const { humanizedNumber, humanizedUnit } = humanizeUtil(
    displayTotal,
    chartMetric,
    metricInfo.unit,
  );
  const value = `${humanizedNumber} ${humanizedUnit}`;

  const metricValue = value && `${value} `;
  const subTitle = `${labelInfo.short} ${labelInfo.subscript}`;

  return (
    <HeaderWrapper>
      <TitleWrapper>
        <StyledMetricTitle>
          <StyledEllipsisTooltip tooltipText={labelInfo.displayName}>
            {labelInfo.displayName}
          </StyledEllipsisTooltip>
          <Subscript title={subTitle}>{labelInfo.subscript}</Subscript>
        </StyledMetricTitle>
      </TitleWrapper>

      <SecondRow>
        {naMode ? (
          <h4 className="na-text">Not Applicable</h4>
        ) : (
          <ValueWrapper>
            {isLiveModeSelected && metricValue && <LiveModeMarker />}
            {metricValue}
            {showTrend && (
              <Trend
                value={trend}
                style={{ textAlign: 'left', paddingLeft: 4 }}
                reverse={metricInfo.reverse}
              />
            )}
            {isUnsupported && <UnsupportedMetricBadge cause={unsupportedCause} />}
            <BooleanFilter metric={metric} />
          </ValueWrapper>
        )}

        {getPercentileMetrics().includes(metric) && (
          <span className="percentile-text">({percentileConfig[percentile].short})</span>
        )}

        <DistributionToggle
          chartMode={chartMode}
          metric={metric}
          distribution={distribution}
          selectDistribution={selectDistribution}
        />
        <Toolbox
          metric={metric}
          chartMode={chartMode}
          setChartMode={setChartMode}
          toggleLegend={toggleLegend}
        />
      </SecondRow>
    </HeaderWrapper>
  );
}

export default Header;
